import { useState, useEffect, useRef } from "react";
import "./Sections.css";

/* Count-up number that animates when scrolled into view (rect-based, IO-free) */
function Counter({ target, suffix = "", comma = false, dur = 1500 }) {
  const [v, setV] = useState(0);
  const ref = useRef(null);
  const done = useRef(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const start = () => {
      if (done.current) return;
      done.current = true;
      const t0 = performance.now();
      const tick = (t) => {
        const p = Math.min((t - t0) / dur, 1);
        const eased = 1 - Math.pow(1 - p, 3);
        setV(Math.round(eased * target));
        if (p < 1) requestAnimationFrame(tick);
      };
      requestAnimationFrame(tick);
    };
    const check = () => {
      const r = el.getBoundingClientRect();
      if (r.top < window.innerHeight * 0.85 && r.bottom > 0) start();
    };
    check();
    window.addEventListener("scroll", check, { passive: true });
    const fb = setTimeout(start, 2800); // safety: never leave it stuck at 0
    return () => { window.removeEventListener("scroll", check); clearTimeout(fb); };
  }, [target, dur]);
  return (
    <b ref={ref}>
      {comma ? v.toLocaleString("en-IN") : v}
      {suffix}
    </b>
  );
}

const API_BASE = "https://feitevmd2k.execute-api.ap-south-1.amazonaws.com/prod";
const GOOGLE_REVIEW_URL = "https://g.page/r/CeKJOJGuvEHAEAE/review";

/* ───────────────────────── About / Intro ───────────────────────── */
export function AboutIntro() {
  const highlights = [
    { icon: "🌄", title: "Born in the Nilgiris", text: "A homegrown Coonoor team that knows every misty trail, tea estate & viewpoint." },
    { icon: "✅", title: "Verified stays & cabs", text: "Hand-picked hill-view stays and background-checked drivers — no surprises." },
    { icon: "💸", title: "Transparent pricing", text: "Clear, upfront packages. What you see is what you pay — taxes included." },
    { icon: "💬", title: "Bilingual support", text: "Real humans on WhatsApp in English & Tamil, before, during and after your trip." },
  ];
  return (
    <section className="about-intro" id="about">
      <div className="about-grid">
        <div className="about-media">
          <img src="https://upload.wikimedia.org/wikipedia/commons/thumb/a/a9/Glendale_Tea_Estate_Kattery_Nilgiris_Jul25_A7CR_06103.jpg/1920px-Glendale_Tea_Estate_Kattery_Nilgiris_Jul25_A7CR_06103.jpg" alt="Nilgiri tea gardens near Ooty" />
          <div className="about-badge">
            <b>4.9★</b>
            <span>on Google Reviews</span>
          </div>
        </div>
        <div className="about-copy">
          <span className="section-eyebrow">About OotyGo</span>
          <h2>Your local guides to the <span className="txt-grad">Queen of Hills</span></h2>
          <p>
            OotyGo is a Nilgiris-based travel company crafting effortless getaways across Ooty,
            Coonoor, Pykara, Kotagiri &amp; beyond. From cozy cottages to private cabs and curated
            sightseeing, we blend local know-how with modern, hassle-free booking — so you just show
            up and soak in the misty mountains.
          </p>
          <div className="about-highlights">
            {highlights.map((h, i) => (
              <div className="about-hl" key={i}>
                <span className="about-hl-icon">{h.icon}</span>
                <div>
                  <h4>{h.title}</h4>
                  <p>{h.text}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ───────────────────────── Stats band ───────────────────────── */
export function StatsBand() {
  const stats = [
    { target: 10, suffix: "K+", l: "Happy Travelers" },
    { target: 2500, suffix: "+", comma: true, l: "Trips Crafted" },
    { target: 80, suffix: "+", l: "Local Partners" },
    { target: 5, suffix: "★", l: "Avg. Experience" },
  ];
  return (
    <section className="stats-band">
      <div className="stats-inner">
        {stats.map((s, i) => (
          <div className="stat-item" key={i}>
            <Counter target={s.target} suffix={s.suffix} comma={s.comma} />
            <span>{s.l}</span>
          </div>
        ))}
      </div>
    </section>
  );
}

/* ───────────────────────── How it works ───────────────────────── */
export function HowItWorks() {
  const steps = [
    { n: "01", icon: "💬", title: "Tell us your plan", text: "Chat with us on the site or WhatsApp — dates, group size & vibe." },
    { n: "02", icon: "📝", title: "Get a custom quote", text: "We design an itinerary with stays, cabs & sightseeing, priced clearly." },
    { n: "03", icon: "🔒", title: "Confirm with 30%", text: "Pay a small advance securely online. Balance only at check-in." },
    { n: "04", icon: "🌿", title: "Travel & enjoy", text: "Your driver & host are briefed. You just relax and explore the hills." },
  ];
  return (
    <section className="how-section" id="how">
      <div className="how-head">
        <span className="section-eyebrow">Simple Process</span>
        <h2 className="how-title">How OotyGo Works</h2>
        <p className="how-sub">From “let’s plan a trip” to “we’re on the hills” in four easy steps.</p>
      </div>
      <div className="how-grid">
        {steps.map((s, i) => (
          <div className="how-card" key={i}>
            <span className="how-num">{s.n}</span>
            <span className="how-icon">{s.icon}</span>
            <h3>{s.title}</h3>
            <p>{s.text}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

/* ───────────────────────── Gallery ───────────────────────── */
export function Gallery() {
  const photos = [
    { src: "https://upload.wikimedia.org/wikipedia/commons/thumb/3/34/Pykara_Lake%2C_Ooty.jpg/1280px-Pykara_Lake%2C_Ooty.jpg", cap: "Pykara Lake" },
    { src: "https://upload.wikimedia.org/wikipedia/commons/thumb/1/14/Gandhipuram_Tea_Estate_Coonoor_Nilgiris_Jun25_A7CR_05879.jpg/1280px-Gandhipuram_Tea_Estate_Coonoor_Nilgiris_Jun25_A7CR_05879.jpg", cap: "Tea Gardens" },
    { src: "https://upload.wikimedia.org/wikipedia/commons/thumb/2/23/Nilgiri_Biosphere_Reserve-Nilgiri_Mountain_Railway-WUS05819.jpg/1280px-Nilgiri_Biosphere_Reserve-Nilgiri_Mountain_Railway-WUS05819.jpg", cap: "Nilgiri Toy Train" },
    { src: "https://upload.wikimedia.org/wikipedia/commons/thumb/d/d8/Fields_Hazy_Mountains_Marlimund_Ooty_Nilgiris_Aug25_A7CR_07258.jpg/1280px-Fields_Hazy_Mountains_Marlimund_Ooty_Nilgiris_Aug25_A7CR_07258.jpg", cap: "Misty Nilgiri Hills" },
    { src: "https://upload.wikimedia.org/wikipedia/commons/thumb/e/ea/Coonoor_Town-1-coonoor-ooty-India.jpg/1280px-Coonoor_Town-1-coonoor-ooty-India.jpg", cap: "Coonoor Town" },
    { src: "https://upload.wikimedia.org/wikipedia/commons/thumb/0/05/Pykara_waterfalls_ooty.jpg/1280px-Pykara_waterfalls_ooty.jpg", cap: "Pykara Falls" },
  ];
  return (
    <section className="gallery-section" id="gallery">
      <div className="how-head">
        <span className="section-eyebrow">Postcards</span>
        <h2 className="how-title">Moments from the Nilgiris</h2>
        <p className="how-sub">A little glimpse of what’s waiting for you up in the hills.</p>
      </div>
      <div className="gallery-grid">
        {photos.map((p, i) => (
          <figure className="gallery-item" key={i}>
            <img src={p.src} alt={p.cap} loading="lazy" />
            <figcaption>{p.cap}</figcaption>
          </figure>
        ))}
      </div>
    </section>
  );
}

/* ───────────────────────── FAQ ───────────────────────── */
export function FAQ() {
  const faqs = [
    { q: "When is the best time to visit Ooty?", a: "Ooty is a year-round destination! April–June is peak summer season (pleasant weather, gardens in bloom), September–November offers misty greenery, and December–February is crisp and cool. We tailor itineraries to the season." },
    { q: "What's included in a package?", a: "Most packages include a private cab with Ooty pickup & drop, hill-view stay, breakfast & dinner, guided sightseeing and a campfire. Entry tickets, lunch and personal expenses are usually extra — every package lists its exact inclusions." },
    { q: "How do I confirm a booking?", a: "Just a 30% advance confirms your trip (securely online). The balance is paid at check-in. The advance is non-refundable, so please confirm your dates before paying." },
    { q: "Can I customise my itinerary?", a: "Absolutely — our Customizable package lets you choose your stay type, meal plan, cab and sightseeing. Share your preferences on WhatsApp and we’ll build it around you." },
    { q: "Which cabs do you offer?", a: "From 4+1 hatchbacks to 7+1, 13+1 and 22+1 vehicles for groups, plus bike rentals (seasonal). Sightseeing cabs run 9 AM–6 PM with transparent extra-hour & extra-km rates." },
    { q: "What's your cancellation policy?", a: "Cancellations made less than 3 days before travel may attract up to 100% of the package cost. We recommend travel insurance and confirming dates in advance." },
  ];
  const [open, setOpen] = useState(0);
  return (
    <section className="faq-section" id="faq">
      <div className="how-head">
        <span className="section-eyebrow">Good to know</span>
        <h2 className="how-title">Frequently Asked Questions</h2>
      </div>
      <div className="faq-list">
        {faqs.map((f, i) => (
          <div className={`faq-item ${open === i ? "open" : ""}`} key={i}>
            <button className="faq-q" onClick={() => setOpen(open === i ? -1 : i)}>
              <span>{f.q}</span>
              <span className="faq-chevron">⌄</span>
            </button>
            <div className="faq-a"><p>{f.a}</p></div>
          </div>
        ))}
      </div>
    </section>
  );
}

/* ───────────────────────── Featured stays ───────────────────────── */
export function FeaturedStays() {
  const stays = [
    { img: "https://images.unsplash.com/photo-1518780664697-55e3ad937233?auto=format&fit=crop&w=900&q=80", name: "Hill-View Cottage", tag: "Most loved", desc: "Wake up to misty valleys & tea slopes from your private balcony." },
    { img: "https://images.unsplash.com/photo-1571003123894-1f0594d2b5d9?auto=format&fit=crop&w=900&q=80", name: "Heritage Bungalow", tag: "Colonial charm", desc: "Restored Nilgiri-era homes with fireplaces and lush gardens." },
    { img: "https://images.unsplash.com/photo-1582719478250-c89cae4dc85b?auto=format&fit=crop&w=900&q=80", name: "Luxury Villa", tag: "For groups", desc: "Spacious private villas — perfect for families & celebrations." },
    { img: "https://images.unsplash.com/photo-1504280390367-361c6d9f38f4?auto=format&fit=crop&w=900&q=80", name: "Tent & Campfire", tag: "Adventure", desc: "Glamping under Nilgiri stars with bonfire and BBQ nights." },
  ];
  return (
    <section className="stays-section" id="stays">
      <div className="how-head">
        <span className="section-eyebrow">Where you'll stay</span>
        <h2 className="how-title">Handpicked Hill Stays</h2>
        <p className="how-sub">From cozy cottages to heritage bungalows — every stay is verified, clean & view-blessed.</p>
      </div>
      <div className="stays-grid">
        {stays.map((s, i) => (
          <article className="stay-card" key={i}>
            <div className="stay-media">
              <img src={s.img} alt={s.name} loading="lazy" />
              <span className="stay-tag">{s.tag}</span>
            </div>
            <div className="stay-body">
              <h3>{s.name}</h3>
              <p>{s.desc}</p>
              <a className="stay-link" href={`https://wa.me/919655062118?text=${encodeURIComponent(`Hi OotyGo, I'm interested in a ${s.name} stay.`)}`} target="_blank" rel="noopener noreferrer">Enquire →</a>
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}

/* ───────────────────────── Live Google reviews ───────────────────────── */
export function ReviewsLive() {
  const [data, setData] = useState(null);
  useEffect(() => {
    fetch(`${API_BASE}/public/google-reviews`)
      .then((r) => r.json())
      .then(setData)
      .catch(() => {});
  }, []);

  const reviews = data?.reviews || [];
  if (!data || !data.ok || reviews.length === 0) return null;

  return (
    <section className="reviews-section" id="reviews">
      <div className="reviews-head">
        <div>
          <span className="section-eyebrow">Real reviews</span>
          <h2 className="how-title">Loved on Google</h2>
        </div>
        <div className="reviews-score">
          <b>{Number(data.rating).toFixed(1)}</b>
          <div>
            <div className="reviews-stars">{"★".repeat(Math.round(data.rating))}</div>
            <span>{data.total} Google reviews</span>
          </div>
        </div>
      </div>
      <div className="reviews-grid">
        {reviews.map((rv, i) => (
          <div className="review-card" key={i}>
            <div className="review-top">
              {rv.photo
                ? <img className="review-av" src={rv.photo} alt={rv.author} referrerPolicy="no-referrer" />
                : <span className="review-av fb">{(rv.author || "G")[0]}</span>}
              <div>
                <h4>{rv.author || "Google user"}</h4>
                <span className="review-stars">{"★".repeat(rv.rating)}<span className="g-badge">G</span></span>
              </div>
            </div>
            {rv.text && <p className="review-text">{rv.text}</p>}
            <span className="review-time">{rv.relativeTime}</span>
          </div>
        ))}
      </div>
      <div className="reviews-cta">
        <a href={GOOGLE_REVIEW_URL} target="_blank" rel="noopener noreferrer" className="reviews-btn">⭐ Write a Google Review</a>
      </div>
    </section>
  );
}

/* ───────────────────────── CTA band ───────────────────────── */
export function CtaBand() {
  return (
    <section className="cta-band">
      <div className="cta-inner">
        <span className="cta-eyebrow">Ready when you are 🌿</span>
        <h2>Let’s plan your Ooty escape</h2>
        <p>Talk to a real local travel expert. Custom quotes in minutes — no bots, no pressure.</p>
        <div className="cta-actions">
          <a className="cta-btn primary" href="https://wa.me/919655062118" target="_blank" rel="noopener noreferrer">💬 Chat on WhatsApp</a>
          <a className="cta-btn ghost" href="tel:+919655062118">📞 +91 96550 62118</a>
        </div>
        <p className="cta-mail">or email <a href="mailto:info@ootygo.com">info@ootygo.com</a></p>
      </div>
    </section>
  );
}
